<template>
  <div class="pc-download-layout">
    <section class="download-hero">
      <div class="app-visual">
        <img src="/images/pexels-mobile-app-night.jpg" alt="点指之间 APP" />
      </div>
      <div class="hero-copy">
        <p class="section-eyebrow">Official Release</p>
        <h1>下载点指之间 APP</h1>
        <p>
          使用手机扫描二维码进入移动发布页，或在电脑上打开通用下载链接查看版本信息。APP 参与路径与移动官网保持一致，注册和下载彼此独立。
        </p>
        <div class="hero-actions">
          <button class="primary-cta" type="button" :disabled="!release.apkUrl" @click="downloadApk">
            {{ release.apkUrl ? '下载 Android 安装包' : '安装包暂未开放' }}
          </button>
          <button class="secondary-cta" type="button" @click="copyShareUrl">复制通用下载链接</button>
        </div>
      </div>
    </section>

    <div class="download-grid">
      <section class="qr-panel">
        <header>
          <div>
            <span>Mobile Link</span>
            <h2>手机扫码下载</h2>
          </div>
        </header>
        <div class="qr-frame">
          <img v-if="qrDataUrl" :src="qrDataUrl" alt="点指之间移动下载页二维码" />
          <div v-else class="qr-loading">{{ qrError ? '二维码生成失败' : '正在生成二维码' }}</div>
        </div>
        <p>{{ shareUrl }}</p>
        <button class="secondary-cta" type="button" @click="copyShareUrl">复制链接</button>
      </section>

      <section class="release-panel">
        <header>
          <div>
            <span>Release</span>
            <h2>版本信息</h2>
          </div>
          <strong>{{ release.versionName || (release.apkUrl ? 'Android 稳定版' : '待发布') }}</strong>
        </header>

        <div v-if="loading" class="release-state">正在获取最新版本...</div>
        <div v-else-if="error" class="release-state error">
          <p>{{ error }}</p>
          <button class="secondary-cta" type="button" @click="loadRelease">重新获取</button>
        </div>
        <template v-else>
          <dl>
            <div>
              <dt>当前版本</dt>
              <dd>{{ release.versionName || (release.apkUrl ? 'Android 稳定版' : '待发布') }}</dd>
            </div>
            <div>
              <dt>安装包大小</dt>
              <dd>{{ release.packageSize || '以实际下载为准' }}</dd>
            </div>
            <div>
              <dt>支持平台</dt>
              <dd>Android</dd>
            </div>
            <div>
              <dt>发布渠道</dt>
              <dd>官方 APK / 应用商店</dd>
            </div>
          </dl>
          <p v-if="release.updateContent" class="update-content">{{ release.updateContent }}</p>
          <div v-if="!release.apkUrl && !availableStores.length" class="release-state">
            当前暂未开放公开下载渠道，你可以先注册账号或联系客服获取发布通知。
            <button class="secondary-cta" type="button" @click="emit('navigate', '/register')">先注册账号</button>
          </div>
        </template>

        <div v-if="availableStores.length" class="store-list">
          <a
            v-for="store in availableStores"
            :key="store.key"
            :href="store.url"
            target="_blank"
            rel="noreferrer"
          >
            <i>{{ store.label?.slice(0, 1) }}</i>
            <span>
              <strong>{{ store.label }}</strong>
              <small>前往应用商店查看</small>
            </span>
            <em>›</em>
          </a>
        </div>
      </section>
    </div>

    <section class="after-register" :class="{ visible: justRegistered }">
      <div>
        <p class="section-eyebrow">Registration Complete</p>
        <h2>账号注册成功</h2>
        <p>下载并登录点指之间 APP 后，即可使用刚才注册的手机号继续完成生态参与。</p>
      </div>
      <button class="primary-cta" type="button" :disabled="!release.apkUrl" @click="downloadApk">
        {{ release.apkUrl ? '立即下载 APP' : '暂无可下载安装包' }}
      </button>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { getAppDownloadInfo } from '@/api/auth'
import { getDownloadShareUrl } from '@/utils/site-url'

const emit = defineEmits(['navigate'])

const loading = ref(true)
const error = ref('')
const qrDataUrl = ref('')
const qrError = ref(false)
const justRegistered = ref(false)
const release = reactive({
  apkUrl: '',
  versionName: '',
  updateContent: '',
  packageSize: '',
  storeList: []
})
const shareUrl = getDownloadShareUrl()

const availableStores = computed(() => (release.storeList || [])
  .map((store) => {
    const packageName = store.packageName || 'cn.vitaeai.dzzj'
    const templateUrl = store.uriTemplate?.replace('%s', encodeURIComponent(packageName))
    const url = store.webUrl || templateUrl || store.intentUrl
    return url ? { ...store, url } : null
  })
  .filter(Boolean))

async function loadRelease() {
  loading.value = true
  error.value = ''
  try {
    const result = await getAppDownloadInfo()
    Object.assign(release, result || {})
    release.storeList = release.storeList || []
  } catch (err) {
    error.value = err?.message || '版本信息获取失败，请稍后重试'
  } finally {
    loading.value = false
  }
}

async function generateQrCode() {
  try {
    const { toDataURL } = await import('qrcode')
    qrDataUrl.value = await toDataURL(shareUrl, {
      width: 210,
      margin: 1,
      color: { dark: '#02050d', light: '#ffffff' }
    })
  } catch {
    qrError.value = true
  }
}

function downloadApk() {
  if (!release.apkUrl) return
  window.open(release.apkUrl, '_blank', 'noopener')
}

async function copyShareUrl() {
  try {
    await navigator.clipboard.writeText(shareUrl)
    window.alert('下载链接已复制')
  } catch {
    window.prompt('请复制下载链接', shareUrl)
  }
}

onMounted(() => {
  const successRaw = sessionStorage.getItem('dzzj_pc_register_success')
  if (successRaw) {
    justRegistered.value = true
    sessionStorage.removeItem('dzzj_pc_register_success')
  }
  loadRelease()
  generateQrCode()
})
</script>

<style scoped>
.pc-download-layout {
  display: grid;
  max-width: 1240px;
  gap: 26px;
  margin: 0 auto;
}

.download-hero {
  display: grid;
  grid-template-columns: minmax(320px, 44%) minmax(0, 1fr);
  align-items: center;
  gap: 56px;
  padding: clamp(30px, 4vw, 58px);
  border: 1px solid rgba(222, 246, 255, 0.15);
  border-radius: 28px;
  background:
    radial-gradient(circle at 82% 12%, rgba(155, 140, 255, 0.12), transparent 34%),
    linear-gradient(135deg, rgba(98, 244, 214, 0.08), transparent 42%),
    rgba(4, 10, 21, 0.82);
  box-shadow: 0 32px 100px rgba(0, 0, 0, 0.36);
}

.app-visual {
  position: relative;
  overflow: hidden;
  min-height: 330px;
  border: 1px solid rgba(222, 246, 255, 0.16);
  border-radius: 22px;
}

.app-visual img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.app-visual::after {
  position: absolute;
  inset: 0;
  content: "";
  background: linear-gradient(180deg, rgba(2, 5, 13, 0.08), rgba(2, 5, 13, 0.62));
}

.app-visual span {
  position: absolute;
  right: 20px;
  bottom: 20px;
  z-index: 1;
  width: 54px;
  height: 54px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(98, 244, 214, 0.42);
  border-radius: 17px;
  color: var(--cyan);
  font-family: var(--latin-font);
  font-size: 12px;
  font-weight: 900;
  background: rgba(2, 6, 15, 0.82);
  backdrop-filter: blur(8px);
}

.hero-copy h1 {
  margin: 18px 0;
  color: var(--ink);
  font-family: var(--display-font);
  font-size: clamp(44px, 5vw, 68px);
  font-weight: 400;
  line-height: 1.05;
}

.hero-copy > p {
  max-width: 620px;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.9;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 34px;
}

.download-grid {
  display: grid;
  grid-template-columns: minmax(300px, 380px) minmax(0, 1fr);
  gap: 26px;
}

.qr-panel,
.release-panel,
.after-register {
  border: 1px solid rgba(222, 246, 255, 0.14);
  border-radius: 24px;
  background: rgba(4, 10, 21, 0.78);
  box-shadow: 0 24px 78px rgba(0, 0, 0, 0.28);
}

.qr-panel,
.release-panel {
  padding: clamp(24px, 3vw, 34px);
}

.qr-panel header,
.release-panel header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.qr-panel header span,
.release-panel header span {
  color: var(--cyan);
  font-family: var(--latin-font);
  font-size: 10.5px;
  font-weight: 850;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.qr-panel h2,
.release-panel h2 {
  margin-top: 6px;
  color: var(--ink);
  font-size: 24px;
}

.qr-panel header i {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(98, 244, 214, 0.3);
  border-radius: 14px;
  color: var(--cyan);
  font-family: var(--latin-font);
  font-size: 11px;
  font-weight: 900;
  background: rgba(98, 244, 214, 0.07);
}

.release-panel header strong {
  color: var(--cyan);
  font-family: var(--latin-font);
  font-size: 20px;
}

.qr-frame {
  width: 238px;
  padding: 14px;
  margin: 0 auto 20px;
  border: 1px dashed rgba(222, 246, 255, 0.22);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.04);
}

.qr-frame img {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
}

.qr-loading {
  display: grid;
  place-items: center;
  aspect-ratio: 1;
  color: var(--quiet);
  font-size: 13px;
  text-align: center;
}

.qr-panel > p {
  margin-bottom: 18px;
  color: var(--quiet);
  font-size: 12.5px;
  word-break: break-all;
  text-align: center;
}

.qr-panel .secondary-cta {
  width: 100%;
}

.release-panel dl {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.release-panel dl div {
  padding: 16px;
  border: 1px solid rgba(222, 246, 255, 0.1);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
}

.release-panel dt {
  color: var(--quiet);
  font-size: 12px;
}

.release-panel dd {
  margin-top: 8px;
  color: var(--ink);
  font-size: 16px;
  font-weight: 800;
}

.update-content {
  margin-top: 18px;
  padding: 17px 18px;
  border-left: 2px solid var(--cyan);
  border-radius: 5px 14px 14px 5px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.8;
  background: rgba(98, 244, 214, 0.055);
  white-space: pre-line;
}

.release-state {
  display: grid;
  gap: 16px;
  padding: 26px;
  border: 1px dashed rgba(222, 246, 255, 0.16);
  border-radius: 16px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.8;
  text-align: center;
}

.release-state.error {
  color: #ff9c9c;
}

.store-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 20px;
}

.store-list a {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 20px;
  align-items: center;
  gap: 13px;
  padding: 14px;
  border: 1px solid rgba(222, 246, 255, 0.12);
  border-radius: 15px;
  color: var(--ink);
  text-decoration: none;
  background: rgba(255, 255, 255, 0.035);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.store-list a:hover {
  border-color: rgba(98, 244, 214, 0.36);
  background: rgba(98, 244, 214, 0.07);
}

.store-list i {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  color: var(--gold);
  font-size: 16px;
  font-style: normal;
  font-weight: 900;
  background: rgba(255, 207, 106, 0.1);
}

.store-list span {
  min-width: 0;
}

.store-list strong {
  display: block;
  font-size: 14px;
}

.store-list small {
  display: block;
  margin-top: 3px;
  color: var(--quiet);
  font-size: 12px;
}

.store-list em {
  color: var(--quiet);
  font-style: normal;
}

.after-register {
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  padding: clamp(24px, 3vw, 34px);
  border-color: rgba(98, 244, 214, 0.28);
  background: linear-gradient(120deg, rgba(98, 244, 214, 0.11), rgba(255, 207, 106, 0.05));
}

.after-register.visible {
  display: flex;
}

.after-register h2 {
  margin: 8px 0 10px;
  color: var(--ink);
  font-size: 28px;
}

.after-register p:last-of-type {
  max-width: 640px;
  color: var(--muted);
  line-height: 1.8;
}

@media (max-width: 1080px) {
  .download-hero {
    grid-template-columns: 1fr;
  }

  .download-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .release-panel dl,
  .store-list {
    grid-template-columns: 1fr;
  }

  .hero-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .after-register.visible {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
